import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { ItemData, Tier, TieredItem, TierList, TierValue, TierValueList } from "../../models/item-data-models";
import { Filter, FavouriteCurrencyMode } from "../../models/filter-models";
import { TieringMode } from "../../models/enums";
import { poe1, poe2 } from "../../../game";
import { logError } from "../../app.config";
import { EconomyService } from "../economy.service";
import { PriceType } from "../../models/economy-models";
import { formatTierThresholdValue, getEffectiveTierThresholdValue } from "../../tier-list-tab/tier-value-helpers";

export const ORDERED_TIERS = [Tier.EVisible, Tier.DWhite, Tier.CYellow, Tier.BOrange, Tier.ARed, Tier.SPurple];

// When crossing from > 1 to < 1 in the same currency, show ~ 1 only if the tier band
// [threshold, previous threshold] is narrow enough to sit around one unit (e.g. 0.5–2 Chaos).
const TILDE_ONE_MAX_BAND_RATIO = 8;

export function currencyTierRank(tier: Tier): number {
  if (tier === Tier.FHidden) return -1;
  return ORDERED_TIERS.indexOf(tier);
}

export function pickHigherCurrencyTier(a: Tier, b: Tier): Tier {
  return currencyTierRank(a) >= currencyTierRank(b) ? a : b;
}

export function promoteCurrencyTier(tier: Tier, steps: number): Tier {
  const newRank = Math.min(currencyTierRank(tier) + steps, ORDERED_TIERS.length - 1);
  return ORDERED_TIERS[Math.max(newRank, 0)];
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyTierService {
  private allItemData: ItemData[];
  private tieredCurrencyItemTypes: string[];

  private defaultTieredCurrency!: TieredItem[];

  private itemsByTieredCurrencyItemType: { baseType: string, itemType: string }[] = [];
  private baseTypesByTieredItemType = new Map<string, string[]>();
  private itemsByCurrencyItemType: { baseType: string, itemType: string }[] = [];
  private divinationCardNames = new Set<string>();

  private tieredCurrency: TierList = [];

  private tierCutoffValues: {
    mode: TieringMode,
    values: TierValueList
  }[] = poe2 ? [
    // ********* POE 2 *********
    {
      mode: 'Early',
      values: [
        { tier: Tier.SPurple, value: 20, baseCurrency: 'Exalted Orb' },
        { tier: Tier.ARed, value: 2.5, baseCurrency: 'Exalted Orb' },
        { tier: Tier.BOrange, value: 0.75, baseCurrency: 'Exalted Orb' },
        { tier: Tier.CYellow, value: 0.2, baseCurrency: 'Exalted Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Exalted Orb' },
        { tier: Tier.EVisible, value: 0.001, baseCurrency: 'Exalted Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Exalted Orb' }
      ]
    },
    {
      mode: 'Normal',
      values: [
        { tier: Tier.SPurple, value: 100, baseCurrency: 'Exalted Orb' },
        { tier: Tier.ARed, value: 15, baseCurrency: 'Exalted Orb' },
        { tier: Tier.BOrange, value: 3, baseCurrency: 'Exalted Orb' },
        { tier: Tier.CYellow, value: 0.5, baseCurrency: 'Exalted Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Exalted Orb' },
        { tier: Tier.EVisible, value: 0.01, baseCurrency: 'Exalted Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Exalted Orb' }
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: Tier.SPurple, value: 3, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 1, baseCurrency: 'Divine Orb' },
        { tier: Tier.BOrange, value: 2, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.05, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'VeryStrict',
      values: [
        { tier: Tier.SPurple, value: 10, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 3, baseCurrency: 'Divine Orb' },
        { tier: Tier.BOrange, value: 1, baseCurrency: 'Divine Orb' },
        { tier: Tier.CYellow, value: 2, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    }
  ] : [
    // ********* POE 1 *********
    {
      mode: 'Early',
      values: [
        { tier: Tier.SPurple, value: 50, baseCurrency: 'Chaos Orb' },
        { tier: Tier.ARed, value: 15, baseCurrency: 'Chaos Orb' },
        { tier: Tier.BOrange, value: 3, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.01, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'Normal',
      values: [
        { tier: Tier.SPurple, value: 100, baseCurrency: 'Chaos Orb' },
        { tier: Tier.ARed, value: 25, baseCurrency: 'Chaos Orb' },
        { tier: Tier.BOrange, value: 5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 1.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 0.75, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.1, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: Tier.SPurple, value: 1, baseCurrency: 'Divine Orb' },
        { tier: Tier.ARed, value: 50, baseCurrency: 'Chaos Orb' },
        { tier: Tier.BOrange, value: 10, baseCurrency: 'Chaos Orb' },
        { tier: Tier.CYellow, value: 2.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.DWhite, value: 1.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.EVisible, value: 0.5, baseCurrency: 'Chaos Orb' },
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' }
      ]
    },
    {
      mode: 'VeryStrict',
      values: [
        { tier: Tier.SPurple, value: 2, baseCurrency: 'Divine Orb' }, // *2
        { tier: Tier.ARed, value: 1/2, baseCurrency: 'Divine Orb' }, // *2
        { tier: Tier.BOrange, value: 1/5, baseCurrency: 'Divine Orb' }, // *4
        { tier: Tier.CYellow, value: 1/20, baseCurrency: 'Divine Orb' }, // *2
        { tier: Tier.DWhite, value: 1/50, baseCurrency: 'Divine Orb' }, // *4
        { tier: Tier.EVisible, value: 1/100, baseCurrency: 'Divine Orb' }, // *4
        { tier: Tier.FHidden, value: 0, baseCurrency: 'Chaos Orb' } //
      ]
    }
  ];

  private currencyTierValueList: TierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;
  private divCardTierValueList: TierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;

  static cacheBreaker = 0;

  constructor(private itemDataService: ItemDataService, private economyService: EconomyService) {
    this.allItemData = this.itemDataService.getItemData();
    this.tieredCurrencyItemTypes = this.itemDataService.getTieredCurrencyItemTypes();

    this.buildItemsByTieredCurrencyItemType();
    this.buildItemsByCurrencyItemType();
  }

  buildItemsByCurrencyItemType() {
    const items: { baseType: string, itemType: string }[] = [];

    this.allItemData.forEach(item => {
      item.baseTypes.forEach(baseType => {
        items.push({ baseType: baseType.name, itemType: item.itemType });
      });
    });

    this.itemsByCurrencyItemType = items;
  }

  buildItemsByTieredCurrencyItemType() {
    const itemsByTieredCurrencyItemType: { baseType: string, itemType: string }[] = [];
    const byBaseType = new Map<string, { baseType: string, itemType: string }>();

    this.allItemData.forEach(item => {
      item.baseTypes.forEach(baseType => {
        const entry = { baseType: baseType.name, itemType: item.tierListItemType || item.itemType };
        itemsByTieredCurrencyItemType.push(entry);
        byBaseType.set(baseType.name, entry);
      });
    });

    this.itemDataService.getTierListItemTypeManual().forEach(item => {
      const existing = byBaseType.get(item.baseType);
      if (existing) {
        existing.itemType = item.tierListItemType;
      } else {
        const entry = { baseType: item.baseType, itemType: item.tierListItemType };
        itemsByTieredCurrencyItemType.push(entry);
        byBaseType.set(item.baseType, entry);
      }
    });

    this.itemsByTieredCurrencyItemType = itemsByTieredCurrencyItemType;

    const baseTypesByTieredItemType = new Map<string, string[]>();
    for (const entry of itemsByTieredCurrencyItemType) {
      const list = baseTypesByTieredItemType.get(entry.itemType);
      if (list) list.push(entry.baseType);
      else baseTypesByTieredItemType.set(entry.itemType, [entry.baseType]);
    }
    this.baseTypesByTieredItemType = baseTypesByTieredItemType;

    this.divinationCardNames = new Set(
      itemsByTieredCurrencyItemType.filter(i => i.itemType === 'Divination Cards').map(i => i.baseType)
    );
  }

  buildDefaultTieredItems() {
    this.defaultTieredCurrency = this.buildDefaultTieredItemsFor(
      this.currencyTierValueList,
      this.divCardTierValueList,
    );
  }

  // Pure simulation: returns a local tier list for the given cutoffs without mutating service state.
  // Economy arrays are read-only here (only name/price/type); results are new TieredItem objects.
  private buildDefaultTieredItemsFor(
    currencyTierValueList: TierValueList,
    divCardTierValueList: TierValueList,
  ): TieredItem[] {
    const currentValues = this.economyService.getCurrencyData();
    const previousValues = this.economyService.getPreviousCurrencyData();
    const previousPriceByName = new Map(previousValues.map(v => [v.name, v.price]));
    const currentByName = new Map(currentValues.map(v => [v.name, v]));

    const exaltValue = poe2 ? 1 : (currentByName.get('Exalted Orb')?.price ?? 1);
    const chaosValue = poe2 ? currentByName.get('Chaos Orb')?.price ?? 10 : 1;
    const divineValue = currentByName.get('Divine Orb')?.price ?? 200;

    const prevExaltValue = poe2 ? 1 : (previousPriceByName.get('Exalted Orb') ?? exaltValue);
    const prevChaosValue = poe2 ? previousPriceByName.get('Chaos Orb') ?? chaosValue : 1;
    const prevDivineValue = previousPriceByName.get('Divine Orb') ?? divineValue;

    const currencyAbs = this.toAbsoluteThresholds(currencyTierValueList, exaltValue, chaosValue, divineValue);
    const divCardAbs = this.toAbsoluteThresholds(divCardTierValueList, exaltValue, chaosValue, divineValue);
    const prevCurrencyAbs = this.toAbsoluteThresholds(currencyTierValueList, prevExaltValue, prevChaosValue, prevDivineValue);
    const prevDivCardAbs = this.toAbsoluteThresholds(divCardTierValueList, prevExaltValue, prevChaosValue, prevDivineValue);

    const defaultTieredCurrency = currentValues.map(item => {
      const isDivCard = this.isDivinationCard(item.name);
      const absThresholds = isDivCard ? divCardAbs : currencyAbs;
      const tier = this.tierForAbsolutePrice(item.price || 0, absThresholds);

      const previousValue = previousPriceByName.get(item.name);
      const previousAbs = isDivCard ? prevDivCardAbs : prevCurrencyAbs;
      const previousTier = previousValue != null ? this.tierForAbsolutePrice(previousValue, previousAbs) : undefined;
      const movedDown = previousTier ? ORDERED_TIERS.indexOf(previousTier) - ORDERED_TIERS.indexOf(tier) : 0;
      const movedUp = previousTier ? ORDERED_TIERS.indexOf(tier) - ORDERED_TIERS.indexOf(previousTier) : 0;

      return {
        name: item.name,
        value: item.price,
        tier,
        priceType: item.type,
        newValue: item.type === PriceType.NewValue,
        previousValue,
        previousTier,
        movedUp,
        movedDown,
      } as TieredItem;
    });

    const byName = new Map(defaultTieredCurrency.map(item => [item.name, item]));
    this.itemDataService.getMinimalCurrencyTierListData().forEach(minimalItemWithTier => {
      const existingItem = byName.get(minimalItemWithTier.name);
      if (existingItem) {
        if (ORDERED_TIERS.indexOf(existingItem.tier as Tier) < ORDERED_TIERS.indexOf(minimalItemWithTier.tier as Tier)) {
          existingItem.tier = minimalItemWithTier.tier;
          existingItem.manualTier = true;
        }
      } else {
        const item = { ...minimalItemWithTier, manualTier: true } as TieredItem;
        defaultTieredCurrency.push(item);
        byName.set(item.name, item);
      }
    });

    return defaultTieredCurrency;
  }

  private toAbsoluteThresholds(
    valueList: TierValueList,
    exaltValue: number,
    chaosValue: number,
    divineValue: number,
  ): { tier: Tier; abs: number }[] {
    return valueList.map(threshold => ({
      tier: threshold.tier,
      abs: getEffectiveTierThresholdValue(threshold) * (
        threshold.baseCurrency === 'Chaos Orb' ? chaosValue
          : threshold.baseCurrency === 'Divine Orb' ? divineValue
            : exaltValue
      ),
    }));
  }

  private tierForAbsolutePrice(price: number, thresholds: { tier: Tier; abs: number }[]): Tier {
    for (const threshold of thresholds) {
      if (price >= threshold.abs) return threshold.tier;
    }
    return Tier.FHidden;
  }

  combineCurrencyTierListData(filter: Filter | null, simulate?: boolean): TieredItem[] {
    const defaultTieredItems = this.defaultTieredCurrency.map(i => ({ ...i } as TieredItem));

    if (simulate) return defaultTieredItems;

    this.applyFavouriteCurrencyTiering(filter!, defaultTieredItems);

    const itemsByName = new Map(defaultTieredItems.map(item => [item.name, item]));

    filter!.customCurrencyTiers.forEach(customItem => {
      const existingItem = itemsByName.get(customItem.name);
      if (existingItem) {
        const economyTier = existingItem.tier;
        existingItem.originalTier = economyTier;
        existingItem.tier = customItem.shownExplicitly
          ? pickHigherCurrencyTier(economyTier, customItem.tier)
          : customItem.tier;
        if (!customItem.shownExplicitly) {
          delete existingItem.favouriteCurrencyAdjusted;
        }
      } else {
        const economyTier = Tier.EVisible;
        const item = {
          name: customItem.name,
          tier: customItem.shownExplicitly
            ? pickHigherCurrencyTier(economyTier, customItem.tier)
            : customItem.tier,
          noValue: true,
          originalTier: economyTier
        } as TieredItem;
        defaultTieredItems.push(item);
        itemsByName.set(item.name, item);
      }
    });

    const hiddenNames = new Set([
      ...this.getHiddenCurrency(filter!),
      ...this.getHiddenRunes(filter!),
      ...this.getHiddenUncutGems(filter!),
    ]);

    if (hiddenNames.size > 0) {
      defaultTieredItems.forEach(item => {
        if (hiddenNames.has(item.name)) {
          item.tier = Tier.FHidden;
          item.hiddenByQuickFilter = true;
        }
      });
    }

    const emptyItemTypes = () => this.tieredCurrencyItemTypes.map(itemType => ({ itemType, baseTypes: [] as TieredItem[] }));
    const tierList: TierList = [
      { tier: Tier.SPurple, itemTypes: emptyItemTypes() },
      { tier: Tier.ARed, itemTypes: emptyItemTypes() },
      { tier: Tier.BOrange, itemTypes: emptyItemTypes() },
      { tier: Tier.CYellow, itemTypes: emptyItemTypes() },
      { tier: Tier.DWhite, itemTypes: emptyItemTypes() },
      { tier: Tier.EVisible, itemTypes: emptyItemTypes() },
      { tier: Tier.FHidden, itemTypes: emptyItemTypes() }
    ];
    const tierDataByTier = new Map(tierList.map(t => [t.tier, t]));
    const itemTypeGroupByTier = new Map(
      tierList.map(t => [t.tier, new Map(t.itemTypes.map(it => [it.itemType, it]))])
    );

    const ruthlessDisabled = poe1 && filter?.ruthless
      ? new Set(this.itemDataService.getRuthlessDisabledCurrencyItems())
      : null;
    const ruthlessOnly = poe1 && !filter?.ruthless
      ? new Set(this.itemDataService.getRuthlessOnlyCurrencyItems())
      : null;

    for (const type of this.itemDataService.getTieredCurrencyItemTypes()) {
      let items = this.baseTypesByTieredItemType.get(type) ?? [];

      if (ruthlessDisabled) {
        items = items.filter(name => !ruthlessDisabled.has(name));
      } else if (ruthlessOnly) {
        items = items.filter(name => !ruthlessOnly.has(name));
      }

      for (const itemName of items) {
        let item = itemsByName.get(itemName);
        if (!item) {
          item = { name: itemName, tier: Tier.ARed, noValue: true } as TieredItem;
          itemsByName.set(itemName, item);
        }

        if (type === 'Divination Cards') {
          item.divCard = this.itemDataService.getDivinationCard(item.name);
        }

        let tierData = tierDataByTier.get(item.tier);
        let typeGroups = itemTypeGroupByTier.get(item.tier);
        if (!tierData || !typeGroups) {
          tierData = { tier: item.tier, itemTypes: emptyItemTypes() };
          tierList.push(tierData);
          tierDataByTier.set(item.tier, tierData);
          typeGroups = new Map(tierData.itemTypes.map(it => [it.itemType, it]));
          itemTypeGroupByTier.set(item.tier, typeGroups);
        }

        // type already comes from itemsByTieredCurrencyItemType (incl. manual overrides)
        let itemTypeGroup = typeGroups.get(type);
        if (!itemTypeGroup) {
          itemTypeGroup = { itemType: type, baseTypes: [] };
          tierData.itemTypes.push(itemTypeGroup);
          typeGroups.set(type, itemTypeGroup);
        }

        itemTypeGroup.baseTypes.push(item);
      }
    }

    // Remove the item types for each tier that are empty

    tierList.forEach(tier => {
      tier.itemTypes = tier.itemTypes.filter(it => it.baseTypes.length > 0);
    });

    // Sort the baseTypes by value, decreasing
    tierList.forEach(tier => {
      tier.itemTypes.forEach(itemType => {
        itemType.baseTypes.sort((a, b) => {
          const valueA = a.value ?? 0;
          const valueB = b.value ?? 0;
          return valueB - valueA;
        });
      });
    });

    this.tieredCurrency.length = 0;
    this.tieredCurrency.push(...tierList);

    CurrencyTierService.cacheBreaker++;

    return defaultTieredItems;
  }

  setTieringMode(mode: TieringMode, filter: Filter) {
    this.currencyTierValueList = this.resolveTierValueList(mode, filter.tieringModeCustom, 'setTieringMode');
    filter.tieringMode = mode;
    this.rebuildTier(filter);
  }

  setDivCardTieringMode(mode: TieringMode, filter: Filter) {
    this.divCardTierValueList = this.resolveTierValueList(mode, filter.divCardTieringModeCustom, 'setDivCardTieringMode');
    filter.divCardTieringMode = mode;
    this.rebuildTier(filter);
  }

  private resolveTierValueList(mode: TieringMode, custom: TierValueList, caller: string): TierValueList {
    const valueList = mode === 'Custom' ? custom : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) {
      // Should never happen, but apparently sometimes does. Use default 'Normal'.
      logError(`CurrencyTierService.${caller}: No value list found for mode ${mode}. Using default Normal.`);
      return this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;
    }
    return valueList;
  }

  private rebuildTier(filter: Filter) {
    this.buildDefaultTieredItems();
    this.combineCurrencyTierListData(filter);
  }

  private isDivinationCard(name: string): boolean {
    return this.divinationCardNames.has(name);
  }

  getTierForPrice(price: number): Tier {
    const { exaltValue, chaosValue, divineValue } = this.getBaseCurrencyValues();
    const toBaseValue = (threshold: TierValue): number => {
      return getEffectiveTierThresholdValue(threshold) * (threshold.baseCurrency === 'Chaos Orb' ? chaosValue : threshold.baseCurrency === 'Divine Orb' ? divineValue : exaltValue);
    };
    return this.currencyTierValueList.filter(threshold => price >= toBaseValue(threshold))[0]?.tier ?? Tier.FHidden;
  }

  // Absolute (exalt/chaos) cutoffs for the active tiering mode — used by value-aware stack promotion
  getAbsoluteTierThresholds(): Map<Tier, number> {
    const { exaltValue, chaosValue, divineValue } = this.getBaseCurrencyValues();
    const map = new Map<Tier, number>();
    for (const threshold of this.currencyTierValueList) {
      const mult = threshold.baseCurrency === 'Chaos Orb' ? chaosValue
        : threshold.baseCurrency === 'Divine Orb' ? divineValue
        : exaltValue;
      map.set(threshold.tier, getEffectiveTierThresholdValue(threshold) * mult);
    }
    return map;
  }

  private getBaseCurrencyValues() {
    const currentValues = this.economyService.getCurrencyData();
    const exaltValue = poe2 ? 1 : (currentValues.find(item => item.name === 'Exalted Orb')?.price ?? 1);
    const chaosValue = poe2 ? currentValues.find(item => item.name === 'Chaos Orb')?.price ?? 10 : 1;
    const divineValue = currentValues.find(item => item.name === 'Divine Orb')?.price ?? 200;
    return { exaltValue, chaosValue, divineValue };
  }

  private applyFavouriteCurrencyTiering(filter: Filter, items: TieredItem[]) {
    const itemTypeByName = new Map(this.itemsByCurrencyItemType.map(i => [i.baseType, i.itemType]));
    const scarabBaseTypeToContent = this.itemDataService.getScarabBaseTypeToContent();

    items.forEach(item => {
      const candidates: Tier[] = [];

      if (filter.favouriteBasicCurrency && filter.favouriteBasicCurrencyTypes.includes(item.name)) {
        candidates.push(this.getFavouriteRuleTier(item.tier, item.value, filter.favouriteBasicCurrencyMode, filter.favouriteBasicCurrencyValue));
      }

      if (filter.favouriteScarabs) {
        const contents = scarabBaseTypeToContent[item.name];
        if (contents?.some(c => filter.favouriteScarabsTypes.includes(c))) {
          candidates.push(this.getFavouriteRuleTier(item.tier, item.value, filter.favouriteScarabsMode, filter.favouriteScarabsValue));
        }
      }

      filter.favouriteCurrencyTypes.forEach(entry => {
        if (!entry.enabled) return;
        const itemType = itemTypeByName.get(item.name);
        if (!itemType || itemType === 'Currency') return;
        if (!entry.itemTypes.includes(itemType)) return;
        candidates.push(this.getFavouriteRuleTier(item.tier, item.value, entry.mode, entry.value));
      });

      if (candidates.length > 0) {
        const economyTier = item.tier;
        const newTier = candidates.reduce((best, tier) => pickHigherCurrencyTier(best, tier), economyTier as Tier);
        if (newTier !== economyTier) {
          item.favouriteCurrencyAdjusted = true;
        }
        item.tier = newTier;
      }
    });
  }

  private getFavouriteRuleTier(currentTier: Tier, itemValue: number | undefined, mode: FavouriteCurrencyMode, value: number | null): Tier {
    switch (mode) {
      case FavouriteCurrencyMode.Multiplier:
        if (value != null && itemValue != null) {
          return this.getTierForPrice(itemValue * value);
        }
        return currentTier;
      case FavouriteCurrencyMode.PromoteTier:
        return promoteCurrencyTier(currentTier, value ?? 1);
      case FavouriteCurrencyMode.ForceVisible:
        return pickHigherCurrencyTier(currentTier, Tier.EVisible);
    }
  }

  getDefaultTieredCurrency() {
    return this.defaultTieredCurrency;
  }

  getTierList() {
    return this.tieredCurrency;
  }

  getFirstCurrencyForDropTier(tier: Tier, divCard = false): string | undefined {
    const tierData = this.tieredCurrency.find(tl => tl.tier === tier);
    if (!tierData) return undefined;

    const baseTypes = divCard
      ? tierData.itemTypes.find(it => it.itemType === 'Divination Cards')?.baseTypes
      : tierData.itemTypes[0]?.baseTypes;

    return CurrencyTierService.pickRepresentativeBaseTypeName(baseTypes);
  }

  private static hasEconomyBasedPrice(item: TieredItem): boolean {
    if (item.noValue) return false;
    if (item.priceType == null) return true;
    return item.priceType !== PriceType.Manual
      && item.priceType !== PriceType.Capped
      && item.priceType !== PriceType.FixedBecauseMissing
      && item.priceType !== PriceType.LeagueBecauseMissing;
  }

  private static pickRepresentativeBaseTypeName(baseTypes: TieredItem[] | undefined): string | undefined {
    if (!baseTypes?.length) return undefined;

    const withEconomyPrice = baseTypes.filter(bt => CurrencyTierService.hasEconomyBasedPrice(bt));
    if (!withEconomyPrice.length) return undefined;

    const withoutApostrophe = withEconomyPrice.filter(bt => !bt.name.includes("'"));
    const candidates = withoutApostrophe.length > 0 ? withoutApostrophe : withEconomyPrice;
    return candidates[candidates.length - 1].name;
  }

  getSelectedTierValueList(divCard = false) {
    return divCard ? this.divCardTierValueList : this.currencyTierValueList;
  }

  getTierCutoffValues() {
    return this.tierCutoffValues;
  }

  getHiddenRunes(filter: Filter) {
    if (!filter.hideRegularRunes) return [];
    const allRegularRunes = this.itemDataService.getAllRegularRunes();
    const hiddenRunes = allRegularRunes.filter(r => filter.hideRegularRunesExceptions.indexOf(r) === -1);
    return hiddenRunes;
  }

  getHiddenUncutGems(filter: Filter) {
    if (!filter.hideUncutGems) return [];
    const hiddenUncutSkillGems = filter.hideUncutSkillGemsBelowLevel ? Array.from({ length: filter.hideUncutSkillGemsBelowLevel }, (_, i) => i + 1).map(i => `Uncut Skill Gem (Level ${i})`) : [];
    const hiddenUncutSpiritGems = filter.hideUncutSpiritGemsBelowLevel ? Array.from({ length: filter.hideUncutSpiritGemsBelowLevel - 3 }, (_, i) => i + 4).map(i => `Uncut Spirit Gem (Level ${i})`) : [];
    const hiddenUncutSupportGems = filter.hideUncutSupportGemsBelowLevel ? Array.from({ length: filter.hideUncutSupportGemsBelowLevel }, (_, i) => i + 1).map(i => `Uncut Support Gem (Level ${i})`) : [];
    const hiddenUncutGems = hiddenUncutSkillGems.concat(hiddenUncutSpiritGems).concat(hiddenUncutSupportGems);
    return hiddenUncutGems;
  }

  getHiddenCurrency(filter: Filter) {
    let hiddenCurrencyTypes = filter.hideCurrency ? filter.hideCurrencyTypes : [];
    if (filter.hideScrolls) hiddenCurrencyTypes = hiddenCurrencyTypes.concat('Scroll of Wisdom');
    if (poe1 && filter.hidePortalScrolls) hiddenCurrencyTypes = hiddenCurrencyTypes.concat('Portal Scroll');

    return hiddenCurrencyTypes;
  }

  unhideFromQuickFilter(filter: Filter, itemName: string): boolean {
    if (this.getHiddenCurrency(filter).includes(itemName)) {
      if (itemName === 'Scroll of Wisdom' && filter.hideScrolls) {
        filter.hideScrolls = false;
        if (poe1) filter.hideScrollsLowerThan = null;
      } else if (itemName === 'Portal Scroll' && filter.hidePortalScrolls) {
        filter.hidePortalScrolls = false;
        if (poe1) filter.hidePortalScrollsLowerThan = null;
      } else if (filter.hideCurrency && filter.hideCurrencyTypes.includes(itemName)) {
        filter.hideCurrencyTypes = filter.hideCurrencyTypes.filter(c => c !== itemName);
      }
      return true;
    }

    if (this.getHiddenRunes(filter).includes(itemName)) {
      if (!filter.hideRegularRunesExceptions.includes(itemName)) {
        const exceptions = filter.hideRegularRunesExceptions.filter(e => e !== 'None');
        filter.hideRegularRunesExceptions = [...exceptions, itemName];
      }
      return true;
    }

    if (this.getHiddenUncutGems(filter).includes(itemName)) {
      return this.unhideUncutGemFromQuickFilter(filter, itemName);
    }

    return false;
  }

  private unhideUncutGemFromQuickFilter(filter: Filter, itemName: string): boolean {
    const match = itemName.match(/Uncut (Skill|Spirit|Support) Gem \(Level (\d+)\)/);
    if (!match) return false;

    const gemType = match[1];
    const level = parseInt(match[2], 10);

    if (gemType === 'Skill' && filter.hideUncutSkillGemsBelowLevel && level <= filter.hideUncutSkillGemsBelowLevel) {
      filter.hideUncutSkillGemsBelowLevel = level > 1 ? level - 1 : null;
      return true;
    }
    if (gemType === 'Spirit' && filter.hideUncutSpiritGemsBelowLevel && level <= filter.hideUncutSpiritGemsBelowLevel) {
      filter.hideUncutSpiritGemsBelowLevel = level > 4 ? level - 1 : null;
      return true;
    }
    if (gemType === 'Support' && filter.hideUncutSupportGemsBelowLevel && level <= filter.hideUncutSupportGemsBelowLevel) {
      filter.hideUncutSupportGemsBelowLevel = level > 1 ? level - 1 : null;
      return true;
    }

    return false;
  }

  getDisplayValueForMode(mode: TieringMode, tier: Tier, filter?: Filter | null, divCard = false) {
    const valueList = mode === 'Custom'
      ? (divCard ? filter?.divCardTieringModeCustom : filter?.tieringModeCustom)
      : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) return null;
    return this.getDisplayValue(tier, valueList);
  }

  // Top-value items that would fall into F-Hidden under the given mode (ignores custom/favourite overrides).
  getMostValuableHiddenItemsForMode(
    mode: TieringMode,
    count = 3,
    filter?: Filter | null,
    divCard = false,
  ): { names: string[]; total: number } {
    const custom = divCard ? filter?.divCardTieringModeCustom : filter?.tieringModeCustom;
    const valueList = this.resolveTierValueList(mode, custom ?? [], 'getMostValuableHiddenItemsForMode');

    const simulated = this.buildDefaultTieredItemsFor(
      divCard ? this.currencyTierValueList : valueList,
      divCard ? valueList : this.divCardTierValueList,
    );

    const hidden = simulated
      .filter(item =>
        item.tier === Tier.FHidden
        && this.isDivinationCard(item.name) === divCard
        && (item.value ?? 0) > 0
      )
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    return {
      names: hidden.slice(0, count).map(item => item.name),
      total: hidden.length,
    };
  }

  getDisplayValue(tier: Tier, valueList?: TierValueList): { priceLabel: string, priceBaseCurrency: string, priceBaseCurrencyImage?: string } {
    const values = valueList ?? this.currencyTierValueList;
    const tierThreshold = values.find(t => t.tier === tier)!;
    let priceLabel = '≥ ' + formatTierThresholdValue(tierThreshold);
    let priceBaseCurrency = tierThreshold.baseCurrency;
    let priceBaseCurrencyImage = this.itemDataService.getFlatItemDataByBaseTypeName(priceBaseCurrency)?.baseType.image;

    const currentTierIndex = values.findIndex(t => t.tier === tier);
    const previousTierThreshold = currentTierIndex >= 0 && currentTierIndex - 1 >= 0
      ? values[currentTierIndex - 1]
      : undefined;

    // Same currency, step down from > 1 to < 1: show ~ 1 when the band up to the previous
    // threshold is narrow around one unit (e.g. 0.5–2 Chaos). Very wide bands (e.g. 0.4–10) keep ≥.
    if (previousTierThreshold && this.showsTildeOneAtCross(previousTierThreshold, tierThreshold)) {
      priceLabel = '~ 1';
    }

    // Upper-bound labels (< previous threshold) only after a ~ 1 row somewhere above, except F-tier which is always "< E threshold".
    const priorHasTildeOne = this.anyPriorTierShowsTildeOne(currentTierIndex, values);
    const tierEffective = getEffectiveTierThresholdValue(tierThreshold);
    const previousEffective = previousTierThreshold ? getEffectiveTierThresholdValue(previousTierThreshold) : undefined;
    const useLessThanPrevious =
      !!previousTierThreshold
      && (tier === Tier.FHidden
        || (previousTierThreshold.baseCurrency === tierThreshold.baseCurrency
          && tierEffective < 1
          && previousEffective! < 1
          && priorHasTildeOne));

    if (useLessThanPrevious) {
      priceLabel = '< ' + formatTierThresholdValue(previousTierThreshold!);
      priceBaseCurrency = previousTierThreshold!.baseCurrency;
      priceBaseCurrencyImage = this.itemDataService.getFlatItemDataByBaseTypeName(priceBaseCurrency)?.baseType.image;
    }

    return {
      priceLabel,
      priceBaseCurrency,
      priceBaseCurrencyImage
    };
  }

  private showsTildeOneAtCross(previousTierThreshold: TierValue, tierThreshold: TierValue): boolean {
    const previousEffective = getEffectiveTierThresholdValue(previousTierThreshold);
    const tierEffective = getEffectiveTierThresholdValue(tierThreshold);
    return previousTierThreshold.baseCurrency === tierThreshold.baseCurrency
      && tierEffective < 1
      && previousEffective > 1
      && previousEffective / tierEffective <= TILDE_ONE_MAX_BAND_RATIO;
  }

  /** Row at index shows ~ 1 when stepping from > 1 to < 1 in a band narrow around one unit. */
  private tierRowShowsTildeOne(rowIndex: number, values: TierValueList = this.currencyTierValueList): boolean {
    if (rowIndex <= 0 || rowIndex >= values.length) return false;
    return this.showsTildeOneAtCross(values[rowIndex - 1], values[rowIndex]);
  }

  private anyPriorTierShowsTildeOne(currentTierIndex: number, values: TierValueList = this.currencyTierValueList): boolean {
    for (let j = 1; j < currentTierIndex; j++) {
      if (this.tierRowShowsTildeOne(j, values)) return true;
    }
    return false;
  }
}
